import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BaseSelectViewModel,
  CreateWorkingSpaceRequest,
  FrozenZoneViewModel,
  IJobStatus,
  LocationClient,
  RoomViewModel,
  UpdateWorkingSpaceRequest,
  WorkingSpaceClient,
  WorkingSpaceViewModel,
} from '@core/api';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
} from 'rxjs';

interface EditModalWorkingSpaceState {
  isLoading: Signal<boolean>;
  modalEditWorkingSpaceWrapper?: Signal<{
    officeAreaTypes: BaseSelectViewModel[];
    changeReasons: BaseSelectViewModel[];
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class EditWorkingSpaceService {
  private locationClient = inject(LocationClient);
  private workingSpaceClient = inject(WorkingSpaceClient);

  readonly load$ = new Subject<string>();
  readonly retrieveData$ = this.load$.pipe(
    switchMap(() =>
      forkJoin({
        officeAreaTypes: this.getOfficeAreaTypes().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        changeReasons: this.getChangeReasons().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
      })
    ),
    shareReplay(1)
  );

  readonly isLoading$ = merge(
    this.load$.pipe(map(() => true)),
    this.retrieveData$.pipe(map(() => false))
  );

  readonly isLoadingToSignal = toSignal(this.isLoading$, {
    initialValue: true,
  });

  isLoadingSubmit = signal(false);

  isLoading = computed(() => {
    const isSubmiting = this.isLoadingSubmit();
    const isLoading = this.isLoadingToSignal();

    return isSubmiting || isLoading;
  });

  readonly retrieveDataSignal = toSignal(this.retrieveData$, {
    initialValue: {
      officeAreaTypes: [] as BaseSelectViewModel[],
      changeReasons: [] as BaseSelectViewModel[],
    },
  });

  state: EditModalWorkingSpaceState = {
    modalEditWorkingSpaceWrapper: this.retrieveDataSignal,
    isLoading: this.isLoading,
  };

  loadDataModalEditWorkingSpaces(id: string): void {
    this.load$.next(id);
  }

  getWorkingSpacesByAreaCode(
    areaCode: string | undefined
  ): Observable<WorkingSpaceViewModel[]> {
    return this.workingSpaceClient.getWorkingSpacesByArea(areaCode);
  }

  updateWorkingSpaces(
    workingSpaces: UpdateWorkingSpaceRequest
  ): Observable<IJobStatus> {
    this.isLoadingSubmit.set(true);
    return this.workingSpaceClient
      .updateWorkingSpaces(workingSpaces)
      .pipe(finalize(() => this.isLoadingSubmit.set(false)));
  }

  createWorkingSpaces(
    workingSpaces: CreateWorkingSpaceRequest[]
  ): Observable<void> {
    return this.workingSpaceClient.createWorkingSpaces(workingSpaces);
  }

  getDummyTypes(): Observable<BaseSelectViewModel[]> {
    return this.workingSpaceClient.getDummyTypes();
  }

  getChangeReasons(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getChangeReasons();
  }

  getOfficeAreaTypes(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getOfficeAreaTypes();
  }

  checkFrozenZone(): Observable<FrozenZoneViewModel> {
    return this.locationClient.checkFrozenZone();
  }

  getRoomInformation(spaceId: string): Observable<RoomViewModel> {
    return this.locationClient.getRoomInformation(spaceId);
  }
}
